import {
  useSidebarContext,
  WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH,
  WORKSPACE_RAIL_DEFAULT_WIDTH,
  WORKSPACE_RAIL_MOBILE_NAV_WIDTH,
  type WorkspaceRail,
} from "@/context/SidebarContext";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { PreviewTooltipProvider } from "@/layouts/WorkspaceLayout/components/PreviewTooltipProvider";
import { WorkspaceRailContent } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceRailContent";
import { WorkspaceRailTrashLink } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/WorkspaceRailTrashLink";
import { WorkspaceRailSearch } from "@/layouts/WorkspaceLayout/components/WorkspaceRailSearch/WorkspaceRailSearch";
import { WorkspaceSidebarFooter } from "@/layouts/WorkspaceLayout/components/WorkspaceSidebarFooter/WorkspaceSidebarFooter";
import { cn } from "@/utils";
import { ResizablePanel } from "@cohesive/ui-components";

interface WorkspaceLeftRailProps {
  className?: string;
}

function RailInner({ rail }: { rail: WorkspaceRail }) {
  return (
    // One shared tooltip provider per rail so row hover-previews share warm
    // state: first preview delayed, neighbors instant while sliding.
    <PreviewTooltipProvider>
      <div
        data-workspace-rail
        className="flex h-full w-full flex-col bg-surface-rail select-none"
        // Suppress the native browser context menu across the rail. Rows/headers
        // with their own right-click menu stop propagation in their onContextMenu,
        // so their events never reach here; everything else bubbles up and is
        // swallowed. Editable fields (e.g. the canvas search) keep the native menu
        // so copy/paste still works.
        onContextMenu={(e) => {
          const target = e.target as HTMLElement;
          if (target.closest("input, textarea, [contenteditable]")) return;
          e.preventDefault();
        }}
      >
        {/* Pinned above the rail-mode switch so search is the one constant
            across every rail (workspaces/skills/canvas/chat) and surface. */}
        <div className="shrink-0 px-2 pt-2">
          <WorkspaceRailSearch />
        </div>
        <WorkspaceRailContent rail={rail} />
        <div className="shrink-0 border-t border-n-10 px-2 py-1">
          <WorkspaceRailTrashLink />
        </div>
        <WorkspaceSidebarFooter />
      </div>
    </PreviewTooltipProvider>
  );
}

export function WorkspaceLeftRail({ className }: WorkspaceLeftRailProps) {
  const lg = useBreakpoint("lg");
  const {
    railOpen,
    activeRail,
    isDrawerOpen,
    setIsDrawerOpen,
    railWidth,
    setRailWidth,
    applyRailWidthToNavZone,
    setRailResizing,
  } = useSidebarContext();

  if (!lg) {
    if (!isDrawerOpen) return null;
    return (
      <>
        <div
          className="fixed inset-0 top-11 z-30 bg-n-95/50 dark:bg-n-00/50"
          onClick={() => setIsDrawerOpen(false)}
          aria-hidden
        />
        <aside
          className={cn(
            "fixed bottom-0 left-0 top-11 z-40 w-64 border-r border-n-10 bg-surface-rail",
            className,
          )}
        >
          <RailInner rail={activeRail} />
        </aside>
      </>
    );
  }

  return (
    <ResizablePanel
      direction="right"
      isClosed={!railOpen}
      initialWidth={WORKSPACE_RAIL_DEFAULT_WIDTH}
      minWidth={WORKSPACE_RAIL_DEFAULT_WIDTH}
      maxWidth={480}
      storageKey="workspace-rail-width"
      showHandleIndicator
      className={cn(
        "h-full shrink-0 bg-surface-rail border-r overflow-visible",
        className,
      )}
      onWidthChange={setRailWidth}
      onWidthChangeEvent={applyRailWidthToNavZone}
      onResizeStart={() => {
        applyRailWidthToNavZone(railWidth);
        setRailResizing(true);
      }}
      onResizeEnd={() => setRailResizing(false)}
    >
      <RailInner rail={activeRail} />
    </ResizablePanel>
  );
}

/** Width of the header nav zone — tracks rail open state and width. */
export function useWorkspaceNavZoneWidth(): number {
  const lg = useBreakpoint("lg");
  const { railOpen, railWidth, isDrawerOpen } = useSidebarContext();

  if (!lg) {
    return isDrawerOpen
      ? WORKSPACE_RAIL_DEFAULT_WIDTH
      : WORKSPACE_RAIL_MOBILE_NAV_WIDTH;
  }
  return railOpen ? railWidth : WORKSPACE_RAIL_COLLAPSED_NAV_WIDTH;
}

/**
 * Horizontal space the rail column actually occupies in the layout row.
 * 0 when collapsed on desktop (the panel closes to zero width) and on mobile
 * (the drawer overlays instead of occupying the row). Unlike the nav zone
 * width, this is the right value to subtract when sizing a pane that should
 * fill the rest of the row.
 */
export function useWorkspaceLeftRailLayoutWidth(): number {
  const lg = useBreakpoint("lg");
  const { railOpen, railWidth } = useSidebarContext();

  if (!lg) {
    return 0;
  }
  return railOpen ? railWidth : 0;
}
